import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { APP_VERSION } from "@/version";

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async () => {
    setErrorMsg('');
    const emailVal = email || document.getElementById('login-email')?.value || '';
    const passVal = password || document.getElementById('login-pass')?.value || '';

    if (!emailVal || !passVal) {
      setErrorMsg('Fylltu inn netfang og lykilorð');
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(emailVal, passVal);
      if (result.success) {
        toast.success('Innskráning tókst!');
        navigate('/');
      } else {
        const msg = result.error || 'Innskráning mistókst';
        setErrorMsg(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err?.message || 'Villa við innskráningu';
      setErrorMsg(msg);
      toast.error(msg);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel — desktop only */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden flex-col items-center justify-center"
        style={{ background: "linear-gradient(180deg, #070d1a 0%, #0c1a30 40%, #0e1f3a 70%, #091628 100%)" }}>
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none" style={{
          width: "320px", height: "120px",
          background: "radial-gradient(ellipse at center bottom, rgba(37,130,255,0.35) 0%, rgba(37,130,255,0.08) 40%, transparent 70%)",
        }} />
        <div className="absolute bottom-[2px] left-1/2 -translate-x-1/2 pointer-events-none" style={{
          width: "140px", height: "2px",
          background: "linear-gradient(90deg, transparent, #3b8bff, transparent)",
          boxShadow: "0 0 20px 4px rgba(59,139,255,0.5)",
        }} />
        <div className="relative z-10 text-center select-none">
          <h1 className="text-white" style={{
            fontSize: "clamp(2.8rem, 5vw, 4.5rem)", fontWeight: 500, letterSpacing: "0.28em",
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
          }}>FONDO</h1>
          <div className="flex items-center justify-center gap-3 mt-3" style={{ color: "#5a6a82" }}>
            <span style={{ width: "20px", height: "1px", background: "#5a6a82", display: "inline-block" }} />
            <span className="text-xs tracking-[0.3em] font-light">EST. 2016</span>
            <span style={{ width: "20px", height: "1px", background: "#5a6a82", display: "inline-block" }} />
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-10">
            <h2 className="text-3xl font-medium tracking-[0.2em]" style={{ color: "#1a2b5e", fontFamily: "'Inter', sans-serif" }}>
              FONDO
            </h2>
            <div className="flex items-center justify-center gap-2 mt-2" style={{ color: "#94a3b8" }}>
              <span style={{ width: "16px", height: "1px", background: "#cbd5e1", display: "inline-block" }} />
              <span className="text-[10px] tracking-[0.2em]">EST. 2016</span>
              <span style={{ width: "16px", height: "1px", background: "#cbd5e1", display: "inline-block" }} />
            </div>
          </div>

          <div className="space-y-1 mb-8">
            <h2 className="text-2xl font-bold" style={{ color: "#1a2b5e" }}>Velkomin</h2>
            <p className="text-sm text-slate-500">Skráðu þig inn til að halda áfram</p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-5" data-testid="login-form-card">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className="text-xs font-medium text-slate-600 uppercase tracking-wide">Netfang</Label>
              <Input
                id="login-email" name="email" type="email" placeholder="fondo@fondo.is"
                value={email} onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading} autoComplete="email"
                className="h-11 border-slate-200 focus:border-blue-600 focus:ring-blue-600/20"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="login-pass" className="text-xs font-medium text-slate-600 uppercase tracking-wide">Lykilorð</Label>
              <Input
                id="login-pass" name="password" type="password" placeholder="••••••••"
                value={password} onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading} autoComplete="current-password"
                className="h-11 border-slate-200 focus:border-blue-600 focus:ring-blue-600/20"
                data-testid="login-password-input"
              />
            </div>

            {errorMsg && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 rounded-md text-sm font-semibold tracking-wide text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              data-testid="login-submit-button"
            >
              {isLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Skrái inn...</>
              ) : 'Innskrá'}
            </button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-8">&copy; {new Date().getFullYear()} FONDO ehf.</p>
        </div>
      </div>
    </div>
  );
};
